import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface Props {
  content: string;
  dark?: boolean;
}

export function Markdown({ content, dark = false }: Props) {
  return (
    <div className={`markdown text-sm leading-relaxed ${dark ? 'markdown-dark' : ''}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code({ className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const codeString = String(children).replace(/\n$/, '');

          if (match) {
            return (
              <div className="my-2 rounded-lg overflow-hidden border border-gray-200">
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 border-b border-gray-200">
                  <span className="text-xs text-gray-500 font-mono">{match[1]}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(codeString)}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <SyntaxHighlighter
                  style={dark ? oneDark : oneLight}
                  language={match[1]}
                  PreTag="div"
                  customStyle={{ margin: 0, borderRadius: 0, fontSize: '0.8rem' }}
                >
                  {codeString}
                </SyntaxHighlighter>
              </div>
            );
          }

          return (
            <code
              className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                dark ? 'bg-white/20' : 'bg-gray-100 text-gray-800'
              }`}
              {...props}
            >
              {children}
            </code>
          );
        },
        a({ href, children }) {
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline ${dark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'}`}
            >
              {children}
            </a>
          );
        },
        table({ children }) {
          return (
            <div className="my-2 overflow-x-auto">
              <table className="min-w-full text-xs border border-gray-200 rounded">{children}</table>
            </div>
          );
        },
        th({ children }) {
          return <th className="px-3 py-1.5 bg-gray-50 text-left font-semibold border-b border-gray-200">{children}</th>;
        },
        td({ children }) {
          return <td className="px-3 py-1.5 border-b border-gray-100">{children}</td>;
        },
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  );
}
